import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { DetailsCardComponent } from '../../../components/details-card/details-card.component';
import {
  QaiLinkButtonComponent,
  QaiPairedFieldsComponent,
  type PairedFieldItem,
} from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { CallWordType } from 'src/app/types/call-word.types';
import { FormGroup, FormArray, FormControl, FormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import {
  take,
  debounceTime,
  distinctUntilChanged,
  Subscription,
  skip,
  switchMap,
  of,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { IWordDefinition } from 'src/app/interfaces/self-serve/responses/iword-definition';
import { SaveAsProductDefaultModalComponent } from '../../../components/modals/save-as-product-default-modal/save-as-product-default-modal.component';
import { IWordsRequest } from 'src/app/interfaces/self-serve/requests/iwords-request';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';

@Component({
  selector: 'app-pronounce-words',
  imports: [
    DetailsCardComponent,
    QaiLinkButtonComponent,
    QaiPairedFieldsComponent,
    CommonModule,
    ReactiveFormsModule,
    SaveAsProductDefaultModalComponent,
  ],
  templateUrl: './pronounce-words.component.html',
  styleUrl: './pronounce-words.component.scss',
})
export class PronounceWordsComponent implements OnInit, OnDestroy {
  title: string = 'Pronounce words';
  description: string =
    'Provide pronunciation corrections for the AI agent so it speaks names/terms correctly. Agent maps common mispronunciations/variants to the New pronunciation.';
  fromLabel: string = 'From';
  toLabel: string = 'To';
  addFromText: string = 'Add mispronunciation';
  addNewPairText: string = 'Add new word';
  callUuid: string = '';
  moduleUuid: string = '';
  personaUuid: string = '';
  form!: FormGroup;
  pronounceWordsPairedFieldsControl: FormControl<PairedFieldItem[]> =
    new FormControl<PairedFieldItem[]>([], {
      nonNullable: true,
    });
  private pronounceWordsSubscription?: Subscription;
  private pronounceWordsPairedFieldsControlSubscription?: Subscription;
  private isUpdatingFromFormArray = signal(false);
  private isUpdatingFromAPI = signal(false);
  private wordTypeUuid: string = '';
  private previousPairedFields: PairedFieldItem[] = [];
  private inFlightCreateRequests = new Set<string>();
  private apiErrors = new Map<string, { key?: string; value?: string }>();
  private duplicateKeyValuePairs = new Set<string>();
  private readonly ERROR_DUPLICATE_KEY_VALUE =
    'Duplicate key combinations are not allowed';
  private readonly DUPLICATE_WORD_STATUS = 409;

  saveAsProductDefaultModalOpen: boolean = false;
  keywordTitle: string = 'pronounce words';
  networkRequestInProgress: boolean = false;
  private apiRequestCount = signal(0);
  readonly isApiRequestInProgress = computed(() => this.apiRequestCount() > 0);

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  get pronounceWordsFormArray(): FormArray {
    return this.form?.get(
      SimulationConstants.PRONOUNCE_WORDS_SECTION
    ) as FormArray;
  }

  get hasNoWords(): boolean {
    const wordItems = this.pronounceWordsPairedFieldsControl?.value || [];
    return (
      wordItems.length === 0 ||
      wordItems.every((item) => !item.key || item.key.trim().length === 0)
    );
  }

  get hasErrors(): boolean {
    return this.apiErrors.size > 0;
  }

  get allWordsHaveProductScope(): boolean {
    if (!this.pronounceWordsFormArray || this.hasNoWords) {
      return false;
    }
    const words =
      this.pronounceWordsFormArray.getRawValue() as IWordDefinition[];
    if (words.length === 0) {
      return false;
    }
    return words.every(
      (word) => word?.scope === SimulationConstants.KEYWORDS_PRODUCT_SCOPE
    );
  }

  get hasProductScopedWords(): boolean {
    const hasProductScopedWordsControl = this.form?.get(
      'pronounce_words_hasProductScopedWords'
    );
    return hasProductScopedWordsControl?.value ?? false;
  }

  /**
   * Get personaUuid directly from form - always reads fresh value
   * This ensures we get the latest value even when emitEvent: false is used
   */
  get currentPersonaUuid(): string {
    return this.getPersonaUuidFromForm();
  }

  /**
   * Check if section should be disabled based on current personaUuid
   * This getter is evaluated every time Angular checks for changes
   */
  get isSectionDisabled(): boolean {
    const personaUuid = this.currentPersonaUuid;
    return !personaUuid || personaUuid.trim().length === 0;
  }

  getFieldError = (
    item: PairedFieldItem,
    index: number,
    field: 'key' | 'value'
  ): string | undefined => {
    const itemId =
      item.uuid ||
      `${item.key?.trim()}|${item.value?.trim()}` ||
      `index-${index}`;
    const apiError = this.apiErrors.get(itemId);

    if (apiError) {
      return field === 'key' ? apiError.key : apiError.value;
    }

    return undefined;
  };

  private updateFormControlDisabledState(): void {
    const shouldDisable =
      this.isSectionDisabled ||
      (!this.hasNoWords && this.allWordsHaveProductScope);
    const wasDisabled = this.pronounceWordsPairedFieldsControl.disabled;
    
    if (shouldDisable) {
      if (!wasDisabled) {
        this.pronounceWordsPairedFieldsControl.disable({ emitEvent: false });
      }
    } else {
      if (wasDisabled) {
        // Before enabling, ensure control has correct value from FormArray
        const items = this.pronounceWordsFormArray?.getRawValue() as IWordDefinition[] || [];
        const expectedPairedFieldItems = items.map((item) => ({
          key: item.key || '',
          value: item.value || '',
          uuid: item.definitionUuid || undefined,
        }));

        if (expectedPairedFieldItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.pronounceWordsPairedFieldsControl.setValue(expectedPairedFieldItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }

        this.pronounceWordsPairedFieldsControl.enable({ emitEvent: false });
        
        if (expectedPairedFieldItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.pronounceWordsPairedFieldsControl.setValue(expectedPairedFieldItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }
      }
    }
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.loadPersonaUuid();
    this.loadModuleUuid();
    this.loadPronounceWords();
  }

  private loadPronounceWords(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.form = form.get(SimulationConstants.KEYWORDS_SECTION) as FormGroup;

        const wordTypeUuidControl = this.form?.get(
          'pronounce_words_wordTypeUuid'
        );
        if (wordTypeUuidControl) {
          this.wordTypeUuid = wordTypeUuidControl.value;
        }

        if (this.pronounceWordsFormArray) {
          this.syncFormArrayToControl();
          this.previousPairedFields =
            this.pronounceWordsPairedFieldsControl.value || [];
          this.setupSyncBetweenFormArrayAndControl();
          this.updateFormControlDisabledState();
        }
      });
  }

  private syncFormArrayToControl(): void {
    if (!this.pronounceWordsFormArray) return;
    this.authorReactiveFormService.syncFormArrayToPairedFieldsControl(
      this.pronounceWordsFormArray,
      this.pronounceWordsPairedFieldsControl,
      this.isUpdatingFromFormArray
    );
  }

  private syncPairedFieldsToFormArray(
    pairedFieldItems: PairedFieldItem[],
    skipApiProcessing: boolean = false
  ): void {
    if (!this.pronounceWordsFormArray) return;
    this.authorReactiveFormService.syncPairedFieldsToFormArray(
      this.pronounceWordsFormArray,
      pairedFieldItems,
      SimulationConstants.KEYWORDS_PERSONA_SCOPE,
      SimulationConstants.PRONOUNCE_WORDS_SECTION,
      this.isUpdatingFromFormArray
    );

    this.cdr.detectChanges();

    if (!skipApiProcessing) {
      requestAnimationFrame(() => {
        if (!this.isUpdatingFromAPI()) {
          this.processPronounceWordsForAPI();
        }
      });
    }
  }

  private setupSyncBetweenFormArrayAndControl(): void {
    if (this.pronounceWordsSubscription) {
      this.pronounceWordsSubscription.unsubscribe();
    }
    if (this.pronounceWordsPairedFieldsControlSubscription) {
      this.pronounceWordsPairedFieldsControlSubscription.unsubscribe();
    }

    this.pronounceWordsSubscription = this.pronounceWordsFormArray.valueChanges
      .pipe(
        skip(1),
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        if (!this.pronounceWordsFormArray) return;

        if (this.isUpdatingFromAPI()) {
          return;
        }

        if (this.isUpdatingFromFormArray()) {
          return;
        }

        this.personaUuid = this.currentPersonaUuid;

        this.syncFormArrayToControl();
        this.updateFormControlDisabledState();
        this.processPronounceWordsForAPI();
      });

    this.pronounceWordsPairedFieldsControlSubscription =
      this.pronounceWordsPairedFieldsControl.valueChanges
        .pipe(
          skip(1),
          debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
          distinctUntilChanged(
            (a, b) => JSON.stringify(a) === JSON.stringify(b)
          )
        )
        .subscribe((pairedFieldItems: PairedFieldItem[]) => {
          if (this.isUpdatingFromAPI()) {
            this.previousPairedFields = pairedFieldItems;
            return;
          }
          if (this.isUpdatingFromFormArray()) {
            this.previousPairedFields = pairedFieldItems;
            return;
          }

          if (
            pairedFieldItems.length === 1 &&
            (!pairedFieldItems[0].key || pairedFieldItems[0].key.trim().length === 0) &&
            (!pairedFieldItems[0].value || pairedFieldItems[0].value.trim().length === 0)
          ) {
            const formArrayWords =
              (this.pronounceWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
            const hasApiWords = formArrayWords.some((word) => word?.definitionUuid);
            if (hasApiWords) {
              this.syncFormArrayToControl();
            }
            return;
          }

          if (this.apiErrors.size > 0) {
            this.previousPairedFields = pairedFieldItems;
            this.clearErrorsForModifiedFields(pairedFieldItems);

            this.syncPairedFieldsToFormArray(pairedFieldItems, true);

            this.validateAndUpdateErrors();

            if (this.apiErrors.size === 0) {
              this.pronounceWordsPairedFieldsControl.updateValueAndValidity();
              this.updateFormControlDisabledState();
            } else {
              return;
            }
          }

          const hasIncompleteFields = pairedFieldItems.some(
            (item) =>
              !item.key ||
              item.key.trim().length === 0 ||
              !item.value ||
              item.value.trim().length === 0
          );
          if (hasIncompleteFields) {
            this.previousPairedFields = pairedFieldItems;
            this.syncPairedFieldsToFormArray(pairedFieldItems, true);
            this.updateFormControlDisabledState();
            return;
          }

          this.personaUuid = this.currentPersonaUuid;

          const removedItems = this.detectRemovals(
            this.previousPairedFields,
            pairedFieldItems
          );
          if (removedItems && removedItems.length > 0) {
            this.onItemsRemoved(removedItems);
            this.previousPairedFields = pairedFieldItems;
            this.updateFormControlDisabledState();
            return;
          }

          this.previousPairedFields = pairedFieldItems;
          this.syncPairedFieldsToFormArray(pairedFieldItems);
          this.updateFormControlDisabledState();
        });
  }

  private getPersonaUuidFromForm(): string {
    if (!this.form) return '';
    const generalSection = this.form.parent?.get(
      SimulationConstants.GENERAL_SECTION
    ) as FormGroup;
    const callPersonaGroup = generalSection?.get(
      SimulationConstants.CALL_PERSONA_SECTION
    ) as FormGroup;
    const personaUuidControl = callPersonaGroup?.get('uuid');
    return personaUuidControl?.value || '';
  }

  private getScopeUuid(scope: string): string {
    switch (scope) {
      case SimulationConstants.KEYWORDS_PRODUCT_SCOPE:
        return this.moduleUuid;
      case SimulationConstants.KEYWORDS_PERSONA_SCOPE:
        return this.currentPersonaUuid;
      default:
        return this.currentPersonaUuid;
    }
  }

  private transformWordsToRequest(
    words: IWordDefinition[],
    includeUuid: boolean = false
  ): IWordsRequest {
    const validWords = words.filter(
      (word) =>
        word.key &&
        word.key.trim().length > 0 &&
        word.value &&
        word.value.trim().length > 0
    );

    const filteredWords = includeUuid
      ? validWords.filter(
          (word) => word.definitionUuid && word.definitionUuid.trim().length > 0
        )
      : validWords;

    let wordTypeUuidToUse = this.wordTypeUuid;
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      const wordTypeUuidControl = this.form?.get(
        'pronounce_words_wordTypeUuid'
      );
      wordTypeUuidToUse = wordTypeUuidControl?.value || '';
    }
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      wordTypeUuidToUse =
        filteredWords.find(
          (word) => word.wordTypeUuid && word.wordTypeUuid.trim().length > 0
        )?.wordTypeUuid || '';
    }

    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      return { wordTypeEntries: [] };
    }

    const wordsWithTypeUuid = filteredWords.map((word) => ({
      ...word,
      wordTypeUuid: word.wordTypeUuid || wordTypeUuidToUse,
    }));

    const wordsByType = new Map<string, IWordDefinition[]>();
    wordsWithTypeUuid.forEach((word) => {
      const wordTypeUuid = word.wordTypeUuid!.trim();
      if (!wordsByType.has(wordTypeUuid)) {
        wordsByType.set(wordTypeUuid, []);
      }
      wordsByType.get(wordTypeUuid)!.push(word);
    });

    const wordTypeEntries = Array.from(wordsByType.entries()).map(
      ([wordTypeUuid, typeWords]) => {
        const firstWord = typeWords[0];
        const scope =
          firstWord.scope || SimulationConstants.KEYWORDS_PERSONA_SCOPE;

        return {
          wordTypeUuid: wordTypeUuid,
          scope,
          scopeUuid: this.getScopeUuid(scope),
          words: typeWords.map((word) => {
            const entry: {
              uuid?: string;
              wordKey: string;
              wordValue?: string | null;
            } = {
              wordKey: word.key.trim(),
            };

            if (includeUuid && word.definitionUuid) {
              entry.uuid = word.definitionUuid;
            }

            if (word.value) {
              entry.wordValue = word.value.trim();
            }

            return entry;
          }),
        };
      }
    );

    return { wordTypeEntries };
  }

  private updateFormWithWordDetails(
    wordDetails: IGetWordDetailsResponse,
    forceReplace: boolean = false
  ): void {
    if (!wordDetails || !Array.isArray(wordDetails)) {
      return;
    }

    const pronounceWordsGroup = wordDetails.find(
      (group) =>
        group.wordTypeName === SimulationConstants.PRONOUNCE_WORDS_SECTION
    );

    if (!pronounceWordsGroup) {
      return;
    }

    if (pronounceWordsGroup.wordTypeUuid) {
      this.wordTypeUuid = pronounceWordsGroup.wordTypeUuid;
      const wordTypeUuidControl = this.form?.get(
        'pronounce_words_wordTypeUuid'
      );
      if (wordTypeUuidControl) {
        wordTypeUuidControl.setValue(pronounceWordsGroup.wordTypeUuid, {
          emitEvent: false,
        });
      }
    }

    if (pronounceWordsGroup.hasProductScopedWords !== undefined) {
      const hasProductScopedWordsControl = this.form?.get(
        'pronounce_words_hasProductScopedWords'
      );
      if (hasProductScopedWordsControl) {
        hasProductScopedWordsControl.setValue(
          pronounceWordsGroup.hasProductScopedWords,
          { emitEvent: false }
        );
      }
    }

    if (!pronounceWordsGroup.words || pronounceWordsGroup.words.length === 0) {
      this.isUpdatingFromAPI.set(true);

      if (this.pronounceWordsFormArray) {
        while (this.pronounceWordsFormArray.length > 0) {
          this.pronounceWordsFormArray.removeAt(0, { emitEvent: false });
        }
      }

      this.pronounceWordsPairedFieldsControl.setValue([], { emitEvent: false });
      this.previousPairedFields = [];

      this.isUpdatingFromAPI.set(false);
      return;
    }

    this.isUpdatingFromAPI.set(true);

    if (!this.pronounceWordsFormArray) {
      this.isUpdatingFromAPI.set(false);
      return;
    }

    const apiWords: IWordDefinition[] = pronounceWordsGroup.words.map(
      (word) => ({
        key: word.wordKey,
        value: word.wordValue,
        scope: word.scope,
        typeName: pronounceWordsGroup.wordTypeName,
        definitionUuid: word.uuid,
        wordTypeUuid: pronounceWordsGroup.wordTypeUuid,
        scopeUuid: word.scopeUuid,
      })
    );

    if (forceReplace) {
      this.pronounceWordsFormArray.clear({ emitEvent: false });
      apiWords.forEach((word) => {
        this.pronounceWordsFormArray.push(
          this.fb.group({
            key: [word.key],
            value: [word.value],
            scope: [word.scope],
            typeName: [word.typeName],
            definitionUuid: [word.definitionUuid],
            wordTypeUuid: [word.wordTypeUuid],
            scopeUuid: [
              word.scopeUuid ||
                this.getScopeUuid(
                  word.scope || SimulationConstants.KEYWORDS_PERSONA_SCOPE
                ),
            ],
          }),
          { emitEvent: false }
        );
      });

      this.syncFormArrayToControl();
      this.previousPairedFields =
        this.pronounceWordsPairedFieldsControl.value || [];
      this.updateFormControlDisabledState();

      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.isUpdatingFromAPI.set(false);
      });
      return;
    }

    if (
      this.pronounceWordsPairedFieldsControl &&
      this.pronounceWordsFormArray
    ) {
      const currentPairedFields =
        this.pronounceWordsPairedFieldsControl.value || [];
      this.syncPairedFieldsToFormArray(currentPairedFields, true);
    }

    const incompleteFields = (
      this.pronounceWordsFormArray.getRawValue() as IWordDefinition[]
    ).filter((w) => w.key?.trim() && !w.value?.trim());
    const apiWordsByUuid = new Map(
      apiWords
        .filter((w) => w.definitionUuid)
        .map((w) => [w.definitionUuid!, w])
    );
    const apiWordsByKeyValue = new Map(
      apiWords
        .filter((w) => w.key?.trim() && w.value?.trim())
        .map((w) => [`${w.key?.trim()}|${w.value?.trim()}`, w])
    );
    const matchedApiWords = new Set<string>();

    for (let i = 0; i < this.pronounceWordsFormArray.length; i++) {
      const formGroup = this.pronounceWordsFormArray.at(i) as FormGroup;
      const word = formGroup.getRawValue() as IWordDefinition;
      const k = word.key?.trim() || '';
      const v = word.value?.trim() || '';
      const matched =
        word.definitionUuid && apiWordsByUuid.has(word.definitionUuid)
          ? apiWordsByUuid.get(word.definitionUuid)
          : k && v && apiWordsByKeyValue.has(`${k}|${v}`)
          ? apiWordsByKeyValue.get(`${k}|${v}`)
          : undefined;

      if (matched) {
        if (word.definitionUuid) matchedApiWords.add(word.definitionUuid);
        if (k && v) matchedApiWords.add(`${k}|${v}`);
        [
          'definitionUuid',
          'wordTypeUuid',
          'scopeUuid',
          'scope',
          'typeName',
        ].forEach((field) => {
          const control = formGroup.get(field);
          if (control && matched[field as keyof IWordDefinition]) {
            control.setValue(matched[field as keyof IWordDefinition], {
              emitEvent: false,
            });
          }
        });
      }
    }

    const existing = new Set(
      (this.pronounceWordsFormArray.getRawValue() as IWordDefinition[]).map(
        (w) => `${w.key?.trim()}|${w.value?.trim()}`
      )
    );
    const apiKeys = new Set(apiWords.map((w) => w.key?.trim()).filter(Boolean));

    apiWords.forEach((word) => {
      const k = word.key?.trim() || '';
      const v = word.value?.trim() || '';
      if (!k || !v) return;
      if (word.definitionUuid && matchedApiWords.has(word.definitionUuid))
        return;
      if (matchedApiWords.has(`${k}|${v}`)) return;
      if (existing.has(`${k}|${v}`)) return;
      this.pronounceWordsFormArray.push(
        this.fb.group({
          key: [word.key],
          value: [word.value],
          scope: [word.scope],
          typeName: [word.typeName],
          definitionUuid: [word.definitionUuid],
          wordTypeUuid: [word.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_PERSONA_SCOPE
              ),
          ],
        }),
        { emitEvent: false }
      );
      existing.add(`${k}|${v}`);
    });

    incompleteFields.forEach((word) => {
      const k = word.key?.trim();
      if (!k || apiKeys.has(k)) return;
      if (existing.has(`${k}|`)) return;
      this.pronounceWordsFormArray.push(
        this.fb.group({
          key: [word.key],
          value: [word.value || null],
          scope: [word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE],
          typeName: [word.typeName || pronounceWordsGroup.wordTypeName],
          definitionUuid: [word.definitionUuid || ''],
          wordTypeUuid: [word.wordTypeUuid || pronounceWordsGroup.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
              ),
          ],
        }),
        { emitEvent: false }
      );
      existing.add(`${k}|`);
    });

    this.syncFormArrayToControl();
    this.updateFormControlDisabledState();

    this.previousPairedFields =
      this.pronounceWordsPairedFieldsControl.value || [];

    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.isUpdatingFromAPI.set(false);
    });
  }

  private createKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, false);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to create');
      return;
    }

    const keyValueKeys: string[] = [];
    words.forEach((word) => {
      const trimmedKey = word.key ? word.key.trim() : '';
      const trimmedValue = word.value ? word.value.trim() : '';
      const keyValueKey = `${trimmedKey}|${trimmedValue}`;
      if (keyValueKey && !this.inFlightCreateRequests.has(keyValueKey)) {
        this.inFlightCreateRequests.add(keyValueKey);
        keyValueKeys.push(keyValueKey);
      }
    });

    this.apiRequestCount.update((count) => count + 1);
    this.authorService
      .createWords(payload)
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.currentPersonaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'createKeyword successful, updating form with word details'
          );
          keyValueKeys.forEach((key) => {
            this.inFlightCreateRequests.delete(key);
          });
          words.forEach((word) => {
            const itemId =
              word.definitionUuid ||
              `${word.key?.trim()}|${word.value?.trim()}`;
            this.apiErrors.delete(itemId);
          });
          this.updateFormWithWordDetails(wordDetails);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error(
            'Error creating keywords or fetching word details:',
            error
          );
          keyValueKeys.forEach((key) => {
            this.inFlightCreateRequests.delete(key);
          });
          if (error?.status === this.DUPLICATE_WORD_STATUS) {
            this.handleDuplicateError(words);
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private updateKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, true);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to update');
      return;
    }

    this.apiRequestCount.update((count) => count + 1);
    this.authorService.updateWords(payload).subscribe({
      next: (response: any) => {
        console.log('updateKeyword successful:', response);
        words.forEach((word) => {
          const itemId =
            word.definitionUuid || `${word.key?.trim()}|${word.value?.trim()}`;
          const error = this.apiErrors.get(itemId);
          if (
            error &&
            !(
              error.key === this.ERROR_DUPLICATE_KEY_VALUE &&
              error.value === this.ERROR_DUPLICATE_KEY_VALUE
            )
          ) {
            this.apiErrors.delete(itemId);
          }
        });
        this.pronounceWordsPairedFieldsControl.updateValueAndValidity();
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
      error: (error: any) => {
        console.error('Error updating keywords:', error);
        if (error?.status === this.DUPLICATE_WORD_STATUS) {
          this.handleDuplicateError(words);
        }
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
    });
  }

  private processPronounceWordsForAPI(): void {
    if (!this.pronounceWordsFormArray) return;

    const pronounceWords =
      this.pronounceWordsFormArray.getRawValue() as IWordDefinition[];

    const validWords = pronounceWords.filter(
      (word) =>
        word.key &&
        word.key.trim().length > 0 &&
        word.value &&
        word.value.trim().length > 0
    );

    if (validWords.length === 0) {
      return;
    }

    const wordsToUpdate: IWordDefinition[] = [];
    const wordsToCreate: IWordDefinition[] = [];
    const processedUuids = new Set<string>();
    const processedKeyValues = new Set<string>();

    validWords.forEach((word) => {
      const trimmedKey = word.key.trim();
      const trimmedValue = word.value ? word.value.trim() : '';
      const keyValueKey = `${trimmedKey}|${trimmedValue}`;

      if (word.definitionUuid && word.definitionUuid.trim().length > 0) {
        if (processedUuids.has(word.definitionUuid)) {
          wordsToCreate.push({ ...word, definitionUuid: '' });
          return;
        }
        if (processedKeyValues.has(keyValueKey)) {
          wordsToCreate.push({ ...word, definitionUuid: '' });
          return;
        }
        wordsToUpdate.push(word);
        processedUuids.add(word.definitionUuid);
        processedKeyValues.add(keyValueKey);
      } else {
        if (processedKeyValues.has(keyValueKey)) {
          return;
        }

        if (this.inFlightCreateRequests.has(keyValueKey)) {
          return;
        }

        wordsToCreate.push(word);
        processedKeyValues.add(keyValueKey);
      }
    });

    if (wordsToUpdate.length > 0) {
      this.updateKeyword(wordsToUpdate);
    }

    if (wordsToCreate.length > 0) {
      this.createKeyword(wordsToCreate);
    }
  }

  onResetToProductDefault(): void {
    if (this.callUuid) {
      const wordType: CallWordType = 'pronounce_words';
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .resetCallKeywords(this.callUuid, wordType, this.currentPersonaUuid)
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.currentPersonaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error resetting to product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  saveAsProductDefault(): void {
    this.saveAsProductDefaultModalOpen = true;
  }

  onSaveAsProductDefaultConfirm(): void {
    if (!this.callUuid || !this.moduleUuid) {
      console.error('Missing callUuid or moduleUuid');
      return;
    }

    this.networkRequestInProgress = true;
    this.apiRequestCount.update((count) => count + 1);

    this.authorService
      .saveAsProductDefault({
        moduleUuid: this.moduleUuid,
        wordType: 'pronounce_words' as CallWordType,
        callUuid: this.callUuid,
        personaUuid: this.currentPersonaUuid,
      })
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.currentPersonaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'Save as product default successful, updating form with word details'
          );
          this.updateFormWithWordDetails(wordDetails, true);
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.saveAsProductDefaultModalOpen = false;
        },
        error: (error: any) => {
          console.error(
            'Error saving as product default or fetching word details:',
            error
          );
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  onSaveAsProductDefaultModalClose(): void {
    this.saveAsProductDefaultModalOpen = false;
  }

  onDetachFromProductDefault(): void {
    if (this.callUuid && this.moduleUuid) {
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .cloneModuleKeywords({
          callUuid: this.callUuid,
          moduleUuid: this.moduleUuid,
          wordType: 'pronounce_words' as CallWordType,
          personaUuid: this.currentPersonaUuid,
        })
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.currentPersonaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            console.log(
              'Detach from product default successful, updating form with word details'
            );
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error detaching from product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  private loadPersonaUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const callPersonaGroup = generalSection?.get(
          SimulationConstants.CALL_PERSONA_SECTION
        ) as FormGroup;
        const personaUuidControl = callPersonaGroup?.get('uuid');
        if (personaUuidControl) {
          this.personaUuid = personaUuidControl.value || '';
        }
      });
  }

  private loadModuleUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const moduleUuidControl = generalSection?.get('moduleUuid');
        if (moduleUuidControl) {
          this.moduleUuid = moduleUuidControl.value || '';
        }
      });
  }

  private detectRemovals(
    previous: PairedFieldItem[],
    current: PairedFieldItem[]
  ): PairedFieldItem[] {
    if (!previous || !current || previous.length === 0) {
      return [];
    }

    const currentMapByUuid = new Map<string, PairedFieldItem>();
    const currentMapByKeyValue = new Map<string, PairedFieldItem>();
    const currentMapByKey = new Map<string, PairedFieldItem>();
    const currentMapByValue = new Map<string, PairedFieldItem[]>();

    current.forEach((item) => {
      const trimmedKey = item.key.trim();
      const trimmedValue = (item.value || '').trim();

      if (item.uuid) {
        currentMapByUuid.set(item.uuid, item);
      }

      const keyValueKey = `key:${trimmedKey}|value:${trimmedValue}`;
      if (!currentMapByKeyValue.has(keyValueKey)) {
        currentMapByKeyValue.set(keyValueKey, item);
      }

      if (!currentMapByKey.has(trimmedKey)) {
        currentMapByKey.set(trimmedKey, item);
      }

      if (trimmedValue) {
        if (!currentMapByValue.has(trimmedValue)) {
          currentMapByValue.set(trimmedValue, []);
        }
        currentMapByValue.get(trimmedValue)!.push(item);
      }
    });

    const removals: PairedFieldItem[] = [];
    previous.forEach((item) => {
      const trimmedKey = item.key.trim();
      const trimmedValue = (item.value || '').trim();
      const keyValueKey = `key:${trimmedKey}|value:${trimmedValue}`;

      if (item.uuid) {
        if (!currentMapByUuid.has(item.uuid)) {
          if (!currentMapByKey.has(trimmedKey)) {
            removals.push(item);
          }
        }
      } else {
        if (!currentMapByKeyValue.has(keyValueKey)) {
          if (!currentMapByKey.has(trimmedKey)) {
            if (trimmedKey === '' && trimmedValue) {
              const itemsWithSameValue = currentMapByValue.get(trimmedValue);
              if (itemsWithSameValue && itemsWithSameValue.length > 0) {
                return;
              }
            }
            removals.push(item);
          }
        }
      }
    });

    return removals;
  }

  private onItemsRemoved(removedItems: PairedFieldItem[]): void {
    if (
      !this.pronounceWordsFormArray ||
      !removedItems ||
      removedItems.length === 0
    ) {
      return;
    }

    const currentWords =
      this.pronounceWordsFormArray.getRawValue() as IWordDefinition[];
    const wordsToDelete: Array<{
      word: IWordDefinition;
      index: number;
    }> = [];

    removedItems.forEach((removedItem) => {
      if (removedItem.uuid && removedItem.uuid.trim().length > 0) {
        const index = currentWords.findIndex(
          (word) =>
            word.definitionUuid &&
            word.definitionUuid.trim() === removedItem.uuid!.trim()
        );
        if (index >= 0) {
          wordsToDelete.push({
            word: currentWords[index],
            index: index,
          });
        }
      } else {
        const index = currentWords.findIndex(
          (word) =>
            word.key &&
            word.key.trim() === removedItem.key.trim() &&
            word.value &&
            word.value.trim() === (removedItem.value || '').trim()
        );
        if (index >= 0) {
          wordsToDelete.push({
            word: currentWords[index],
            index: index,
          });
        }
      }
    });

    if (wordsToDelete.length === 0) {
      return;
    }

    const wordsByScope = new Map<string, string[]>();
    const uuidSet = new Set<string>();

    wordsToDelete.forEach(({ word }) => {
      if (
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0 &&
        !uuidSet.has(word.definitionUuid)
      ) {
        uuidSet.add(word.definitionUuid);
        const scopeUuid =
          word.scopeUuid ||
          this.getScopeUuid(
            word.scope || SimulationConstants.KEYWORDS_PERSONA_SCOPE
          );

        if (scopeUuid && scopeUuid.trim().length > 0) {
          if (!wordsByScope.has(scopeUuid)) {
            wordsByScope.set(scopeUuid, []);
          }
          wordsByScope.get(scopeUuid)!.push(word.definitionUuid);
        }
      }
    });

    wordsByScope.forEach((uuids, scopeValue) => {
      this.deleteWords(uuids, scopeValue);
    });

    this.isUpdatingFromFormArray.set(true);
    wordsToDelete
      .sort((a, b) => b.index - a.index)
      .forEach(({ index }) => {
        this.pronounceWordsFormArray.removeAt(index, { emitEvent: false });
      });
    this.isUpdatingFromFormArray.set(false);

    const currentPairedFields =
      this.pronounceWordsPairedFieldsControl.value || [];
    this.previousPairedFields = currentPairedFields;

    this.syncFormArrayToControl();
    this.updateFormControlDisabledState();
  }

  onPairRemoved(removedItem: PairedFieldItem): void {
    if (!this.pronounceWordsFormArray || !removedItem || !removedItem.key) {
      return;
    }

    const currentWords =
      this.pronounceWordsFormArray.getRawValue() as IWordDefinition[];

    const wordsToDelete: Array<{
      word: IWordDefinition;
      index: number;
    }> = [];

    if (removedItem.uuid && removedItem.uuid.trim().length > 0) {
      currentWords.forEach((word, index) => {
        if (
          word.definitionUuid &&
          word.definitionUuid.trim() === removedItem.uuid!.trim()
        ) {
          wordsToDelete.push({ word, index });
        }
      });
    } else {
      const trimmedKey = removedItem.key.trim();
      const trimmedValue = (removedItem.value || '').trim();

      currentWords.forEach((word, index) => {
        const wordKey = word.key ? word.key.trim() : '';
        const wordValue = word.value ? word.value.trim() : '';
        if (wordKey === trimmedKey && wordValue === trimmedValue) {
          wordsToDelete.push({ word, index });
        }
      });
    }

    if (wordsToDelete.length === 0) {
      const currentPairedFields =
        this.pronounceWordsPairedFieldsControl.value || [];
      this.previousPairedFields = currentPairedFields.filter(
        (item) =>
          !(item.key === removedItem.key && item.value === removedItem.value)
      );
      return;
    }

    const wordsByScope = new Map<string, string[]>();
    const uuidSet = new Set<string>();

    wordsToDelete.forEach(({ word }) => {
      if (
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0 &&
        !uuidSet.has(word.definitionUuid)
      ) {
        uuidSet.add(word.definitionUuid);
        const scopeUuid =
          word.scopeUuid ||
          this.getScopeUuid(
            word.scope || SimulationConstants.KEYWORDS_PERSONA_SCOPE
          );

        if (scopeUuid && scopeUuid.trim().length > 0) {
          if (!wordsByScope.has(scopeUuid)) {
            wordsByScope.set(scopeUuid, []);
          }
          wordsByScope.get(scopeUuid)!.push(word.definitionUuid);
        }
      }
    });

    wordsByScope.forEach((uuids, scopeValue) => {
      this.deleteWords(uuids, scopeValue);
    });

    this.isUpdatingFromFormArray.set(true);
    wordsToDelete
      .sort((a, b) => b.index - a.index)
      .forEach(({ index }) => {
        this.pronounceWordsFormArray.removeAt(index, { emitEvent: false });
      });
    this.isUpdatingFromFormArray.set(false);

    const currentPairedFields =
      this.pronounceWordsPairedFieldsControl.value || [];
    this.previousPairedFields = currentPairedFields.filter((item) => {
      if (removedItem.uuid) {
        return item.uuid !== removedItem.uuid;
      } else {
        const itemKey = item.key ? item.key.trim() : '';
        const itemValue = (item.value || '').trim();
        const removedKey = removedItem.key ? removedItem.key.trim() : '';
        const removedValue = (removedItem.value || '').trim();
        return !(itemKey === removedKey && itemValue === removedValue);
      }
    });

    this.syncFormArrayToControl();
    this.updateFormControlDisabledState();
  }

  private deleteWords(uuids: string[], scopeValue: string): void {
    if (uuids.length === 0 || !scopeValue) {
      return;
    }

    const currentWords =
      (this.pronounceWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
    const remainingWords = currentWords.filter(
      (word) =>
        word.definitionUuid &&
        word.definitionUuid.trim().length > 0 &&
        !uuids.includes(word.definitionUuid)
    );
    const willHaveNoWords = remainingWords.length === 0;

    this.apiRequestCount.update((count) => count + 1);
    this.authorService
      .deleteWords({
        uuids,
        scopeValue,
      })
      .pipe(
        switchMap(() => {
          if (willHaveNoWords) {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.currentPersonaUuid
            );
          }
          return of([]);
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse | any[]) => {
          if (willHaveNoWords && Array.isArray(wordDetails)) {
            console.log(
              'Delete words successful, updating form with word details'
            );
            this.updateFormWithWordDetails(wordDetails, true);
          } else {
            console.log('Delete words successful');
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error('Error deleting words:', error);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private clearErrorsForModifiedFields(currentItems: PairedFieldItem[]): void {
    const currentItemIds = new Set<string>();
    const currentKeyValuePairs = new Set<string>();

    currentItems.forEach((item) => {
      const trimmedKey = item.key?.trim() || '';
      const trimmedValue = item.value?.trim() || '';
      if (trimmedKey && trimmedValue) {
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        currentKeyValuePairs.add(keyValueKey);
        const itemId = item.uuid || keyValueKey;
        currentItemIds.add(itemId);
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      const isDuplicateError =
        error.key === this.ERROR_DUPLICATE_KEY_VALUE &&
        error.value === this.ERROR_DUPLICATE_KEY_VALUE;

      if (isDuplicateError) {
        return;
      }

      const isUuid = !itemId.includes('|');

      if (isUuid) {
        if (!currentItemIds.has(itemId)) {
          itemsToClear.push(itemId);
        }
      } else {
        if (!currentKeyValuePairs.has(itemId)) {
          itemsToClear.push(itemId);
        }
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));
  }

  private handleDuplicateError(words: IWordDefinition[]): void {
    const allFormWords =
      (this.pronounceWordsFormArray?.getRawValue() as IWordDefinition[]) || [];

    const duplicateKeyValuePairs = this.detectDuplicateKeyValuePairs(
      words,
      allFormWords
    );

    if (duplicateKeyValuePairs.size > 0) {
      duplicateKeyValuePairs.forEach((keyValueKey) =>
        this.duplicateKeyValuePairs.add(keyValueKey)
      );
      this.setErrorsOnDuplicateFields(
        duplicateKeyValuePairs,
        allFormWords,
        words
      );
    }

    this.pronounceWordsPairedFieldsControl.updateValueAndValidity();
  }

  private setErrorsOnDuplicateFields(
    duplicateKeyValuePairs: Set<string>,
    allFormWords: IWordDefinition[],
    requestWords: IWordDefinition[]
  ): void {
    const allWordsToCheck = [...allFormWords, ...requestWords];

    duplicateKeyValuePairs.forEach((keyValueKey) => {
      allWordsToCheck.forEach((word) => {
        const trimmedKey = word.key?.trim() || '';
        const trimmedValue = word.value?.trim() || '';
        const wordKeyValueKey = `${trimmedKey}|${trimmedValue}`;

        if (wordKeyValueKey === keyValueKey) {
          const itemId = word.definitionUuid || wordKeyValueKey;
          this.apiErrors.set(itemId, {
            key: this.ERROR_DUPLICATE_KEY_VALUE,
            value: this.ERROR_DUPLICATE_KEY_VALUE,
          });
        }
      });
    });
  }

  private validateAndUpdateErrors(): void {
    const currentWords =
      this.pronounceWordsFormArray.getRawValue() as IWordDefinition[];
    const duplicateFields = this.detectDuplicateKeyValuePairs(currentWords);

    const uuidToKeyValue = new Map<string, string>();
    const currentKeyValuePairs = new Set<string>();
    const currentItemIds = new Set<string>();

    currentWords.forEach((word) => {
      const trimmedKey = word.key?.trim() || '';
      const trimmedValue = word.value?.trim() || '';
      if (trimmedKey && trimmedValue) {
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        currentKeyValuePairs.add(keyValueKey);
        const itemId = word.definitionUuid || keyValueKey;
        currentItemIds.add(itemId);
        if (word.definitionUuid) {
          uuidToKeyValue.set(word.definitionUuid, keyValueKey);
        }
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      const isUuid = !itemId.includes('|');

      if (isUuid) {
        if (!currentItemIds.has(itemId)) {
          itemsToClear.push(itemId);
        } else {
          const currentKeyValue = uuidToKeyValue.get(itemId);
          if (currentKeyValue && !duplicateFields.has(currentKeyValue)) {
            if (
              error.key === this.ERROR_DUPLICATE_KEY_VALUE ||
              error.value === this.ERROR_DUPLICATE_KEY_VALUE
            ) {
              itemsToClear.push(itemId);
            }
          }
        }
      } else {
        if (!currentKeyValuePairs.has(itemId)) {
          itemsToClear.push(itemId);
        } else if (!duplicateFields.has(itemId)) {
          if (
            error.key === this.ERROR_DUPLICATE_KEY_VALUE ||
            error.value === this.ERROR_DUPLICATE_KEY_VALUE
          ) {
            itemsToClear.push(itemId);
          }
        }
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));

    this.duplicateKeyValuePairs.clear();
    duplicateFields.forEach((keyValueKey) =>
      this.duplicateKeyValuePairs.add(keyValueKey)
    );

    duplicateFields.forEach((keyValueKey) => {
      currentWords.forEach((word) => {
        const trimmedKey = word.key?.trim() || '';
        const trimmedValue = word.value?.trim() || '';
        if (`${trimmedKey}|${trimmedValue}` === keyValueKey) {
          const itemId = word.definitionUuid || keyValueKey;
          if (!this.apiErrors.has(itemId)) {
            this.apiErrors.set(itemId, {
              key: this.ERROR_DUPLICATE_KEY_VALUE,
              value: this.ERROR_DUPLICATE_KEY_VALUE,
            });
          }
        }
      });
    });
  }

  private detectDuplicateKeyValuePairs(
    words: IWordDefinition[],
    allFormWords?: IWordDefinition[]
  ): Set<string> {
    const duplicates = new Set<string>();
    const formWords =
      allFormWords && allFormWords !== words
        ? allFormWords
        : (this.pronounceWordsFormArray?.getRawValue() as IWordDefinition[]) ||
          [];

    const keyValueCounts = new Map<string, number>();
    const keyCounts = new Map<string, number>();

    formWords.forEach((w) => {
      const trimmedKey = w.key?.trim() || '';
      const trimmedValue = w.value?.trim() || '';
      if (trimmedKey && trimmedValue) {
        const keyValueKey = `${trimmedKey}|${trimmedValue}`;
        keyValueCounts.set(
          keyValueKey,
          (keyValueCounts.get(keyValueKey) || 0) + 1
        );
        keyCounts.set(trimmedKey, (keyCounts.get(trimmedKey) || 0) + 1);
      }
    });

    keyValueCounts.forEach((count, keyValueKey) => {
      if (count > 1) {
        duplicates.add(keyValueKey);
      }
    });

    keyCounts.forEach((count, key) => {
      if (count > 1) {
        formWords.forEach((w) => {
          const trimmedKey = w.key?.trim() || '';
          const trimmedValue = w.value?.trim() || '';
          if (trimmedKey === key && trimmedValue) {
            const keyValueKey = `${trimmedKey}|${trimmedValue}`;
            duplicates.add(keyValueKey);
          }
        });
      }
    });

    return duplicates;
  }

  ngOnDestroy(): void {
    if (this.pronounceWordsSubscription) {
      this.pronounceWordsSubscription.unsubscribe();
    }
    if (this.pronounceWordsPairedFieldsControlSubscription) {
      this.pronounceWordsPairedFieldsControlSubscription.unsubscribe();
    }
  }
}
